import { useEffect, useMemo, useState } from "react";

export function useMediaQuery(query) {
	const [matches, setMatches] = useState(() =>
		typeof window !== "undefined" ? window.matchMedia(query).matches : false
	);

	useEffect(() => {
		const mql = window.matchMedia(query);
		const onChange = (e) => setMatches(e.matches);
		setMatches(mql.matches);
		mql.addEventListener("change", onChange);
		return () => mql.removeEventListener("change", onChange);
	}, [query]);

	return matches;
}

export const useReducedMotion = () => useMediaQuery("(prefers-reduced-motion: reduce)");

/**
 * WebGL 지원 여부를 모듈 로드 시 한 번만 확인한다.
 * 감지용 컨텍스트는 곧바로 해제한다 — 안 그러면 브라우저의
 * 동시 컨텍스트 한도(보통 16개)를 감지 코드가 갉아먹는다.
 */
let webglSupport = null;

function detectWebGL() {
	if (webglSupport !== null) return webglSupport;
	if (typeof document === "undefined") return false;

	try {
		const canvas = document.createElement("canvas");
		const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");
		webglSupport = Boolean(gl);
		gl?.getExtension("WEBGL_lose_context")?.loseContext();
	} catch {
		webglSupport = false;
	}

	return webglSupport;
}

/**
 * WebGL을 실제로 그릴지 판단.
 * 저사양/모바일/모션축소 환경에서는 3D 씬을 통째로 건너뛴다.
 */
export function useCanRender3D() {
	const reduced = useReducedMotion();
	const coarse = useMediaQuery("(max-width: 767px)");
	const supported = useMemo(detectWebGL, []);

	return supported && !reduced && !coarse;
}

/** 스크롤 위치가 임계값을 넘었는지 */
export function useScrolled(threshold = 24) {
	const [scrolled, setScrolled] = useState(false);
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > threshold);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [threshold]);
	return scrolled;
}
